import { inject, Injectable } from "@angular/core";
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { debounceTime, distinctUntilChanged, Observable, startWith, Subscription, switchMap } from "rxjs";
import { DriverList } from "../../dashboard/models/driver-list";
import {
    TRIP_RATE,
    TripAddressDto,
    TripAppointmentDto,
    TripLegsDto,
    TripPassengerDto,
    TripQueueMapping,
    TripRequestDto,
    TripRequestStatus,
    TripRequestType
} from "../../dashboard/models/trip-request-models";
import { OTHER_ADDRESS_CATEGORY } from "../../dashboard/profile/state/profile-model";
import { ArcGISService } from "../../dashboard/services/arcGIS-service";
import { TripRequestService } from "../../dashboard/services/trip-request.service";
import { AuthService } from "../../shared/services/auth.service";
import { LookupsService } from "../../shared/services/lookups-service";
import { SelectedProfileService } from "../../shared/services/selected-profile.service";
import { multyStateCodesValidator } from "../../shared/validators/state-validators";
import { County } from "../../state/lookups/lookups-model";


@Injectable({
  providedIn: 'root',
})
export class TripRequestFormService { 
    private authService = inject(AuthService);
    private arcGISService = inject(ArcGISService);
    private lookupService = inject(LookupsService);
    private tripRequestService = inject(TripRequestService);
    private selectedProfileService = inject(SelectedProfileService);
    selectedAppointmentType = '';
    tripRate = TRIP_RATE;
    consumerCounty!: County;
    private fb = inject(FormBuilder);
    consumerEnteredMilesSub: Subscription[] = [];


    getSearchAddressList(fg: FormGroup): Observable<string[]> {
        return fg.get('address')!?.valueChanges.pipe(
        startWith(''),
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((value) => {
            if (fg.get('previousAddress')?.value === null) {
            if (fg.get('address')?.value) {
                return this.arcGISService.searchAddress(fg.get('address')?.value);
            }
            }
            if (fg.get('previousAddress')?.value != value) {
            if (value) {
                return this.arcGISService.searchAddress(value);
            }
            }
            return new Observable<string[]>();
        }),
        );
    }

    saveAppointments(tripRequestFg: FormGroup, tripType: TripRequestType, isAcceptedPreTrip: boolean = false): Observable<TripRequestDto> {
        const tripRqModel: TripRequestDto = {
            tripRequestId: tripRequestFg.get('tripRequestId')?.value,
            recipientId: this.selectedProfileService.selectedRecipientId()!,
            tripType: tripType,
            tripRequestStatusId: TripRequestStatus.Unsubmitted,
            isAcceptedPreTrip: isAcceptedPreTrip,
            stepIndex: 1
        };

        const appointmentRqModel: TripAppointmentDto[] = [];
        const tripAppointments = tripRequestFg.get('tripAppointments') as FormArray;

        tripAppointments.controls.forEach((control: AbstractControl) => {
            const appointment: TripAppointmentDto = {
                appointmentId: control.get('appointmentId')?.value,
                appointmentDate: control.get('appointmentDate')?.value,
                appointmentTime: control.get('appointmentTime')?.value,
            };
            appointmentRqModel.push(appointment);
        });

        tripRqModel.tripAppointments = appointmentRqModel;

        return this.tripRequestService.saveAppointments(tripRqModel);
    }

    saveTripDriverInfo(tripRequestFg: FormGroup): Observable<TripRequestDto> {
        const tripRqModel: TripRequestDto = {
            tripRequestId: tripRequestFg.get('tripRequestId')?.value,
            recipientId: this.selectedProfileService.selectedRecipientId()!,
            tripType: TripRequestType.Post,
            tripRequestStatusId: tripRequestFg.get('tripRequestStatus')?.value,
            driverId: 0,
            driverFirstName: tripRequestFg.get('driverFirstName')?.value,
            driverLastName: tripRequestFg.get('driverLastName')?.value,
            isSameDriver: tripRequestFg.get('isSameDriver')?.value,
            saveDriverIntoProfile: tripRequestFg.get('saveDriverIntoProfile')?.value,
            stepIndex: 2
        };

        const appointmentRqModel: TripAppointmentDto[] = [];
        const tripAppointments = tripRequestFg.get('tripAppointments') as FormArray;

        tripAppointments.controls.forEach((control: AbstractControl) => {
        const driverInfo: DriverList = {
            id: 0,
            firstName: control.get('driverFirstName')?.value,
            lastName: control.get('driverLastName')?.value,
            fullName: '',
        };

        const appointment: TripAppointmentDto = {
            appointmentId: control.get('appointmentId')?.value,
            appointmentDate: control.get('appointmentDate')?.value,
            appointmentTime: control.get('appointmentTime')?.value,
            saveDriverIntoProfile: control.get('saveDriverIntoProfile')?.value,
            appointmentDriver: driverInfo,
        };
        appointmentRqModel.push(appointment);
        });
        tripRqModel.tripAppointments = appointmentRqModel;

        return this.tripRequestService.saveDriverInformation(tripRqModel);
    }

    validateAppointmentsData(appointmentsFa: FormArray): boolean {
        for (let i = 0; i <= appointmentsFa.length - 1; i++) {
            const appFg = appointmentsFa.at(i) as FormGroup;
            const dateFc = appointmentsFa.at(i).get('appointmentDate') as FormControl;
            const timeFc = appointmentsFa.at(i).get('appointmentTime') as FormControl;
            if (appFg.hasError('futureTimeRequired') || appFg.hasError('pastTimeRequired')) {
                timeFc.setErrors({ timeRequired: true });
            }
            if (dateFc.invalid || timeFc.invalid) {
                dateFc.markAsTouched();
                timeFc.markAsTouched();
                return false;
            }
        }
        return true;
    }

    validateDriverInformation(tripRequestFg: FormGroup): boolean {
        if (tripRequestFg.get('isSameDriver')?.value) {
        if (!tripRequestFg.get('driverMannualEntry')?.value) {
            if (tripRequestFg.get('driverId')?.invalid) {
            tripRequestFg.get('driverId')?.markAsTouched();
            return false;
            }
        } else if (tripRequestFg.get('driverFirstName')?.invalid || tripRequestFg.get('driverLastName')?.invalid) {
            tripRequestFg.get('driverFirstName')?.markAsTouched();
            tripRequestFg.get('driverLastName')?.markAsTouched();
            return false;
        }
        } else {
        const appointmentsFa = tripRequestFg.get('tripAppointments') as FormArray;
        for (let i = 0; i <= appointmentsFa.length - 1; i++) {
            if (!appointmentsFa.at(i).get('driverMannualEntry')?.value) {
            if (appointmentsFa.at(i).get('driverId')?.invalid) {
                appointmentsFa.at(i).get('driverId')?.markAsTouched();
                return false;
            }
            } else if (appointmentsFa.at(i).get('driverFirstName')?.invalid || appointmentsFa.at(i).get('driverLastName')?.invalid) {
            appointmentsFa.at(i).get('driverFirstName')?.markAsTouched();
            appointmentsFa.at(i).get('driverLastName')?.markAsTouched();
            return false;
            }
        }
        }
        return true;
    }

    validateTripLocation(tripRequestFg: FormGroup): boolean {
        if (tripRequestFg.get('appointmentTypeId')?.invalid || (this.selectedAppointmentType === 'Other' && tripRequestFg.get('appointmentType')?.invalid)) {
            this.markLocationControlsTouched(tripRequestFg);
            return false;
        }
        const addressFa = tripRequestFg.get('tripAddresses') as FormArray;
        for (let i = 0; i <= addressFa.length - 1; i++) {
            if (
                addressFa.at(i).get('addressCategoryId')?.invalid ||
                addressFa.at(i).get('address')?.invalid ||
                (addressFa.at(i).get('providerOrNickName')?.invalid && addressFa.at(i).get('locationMannualEntry')?.value) ||
                (addressFa.at(i).get('addressCategoryId')?.value === OTHER_ADDRESS_CATEGORY && addressFa.at(i).get('otherCategoryType')?.invalid)
            ) {
                this.markLocationControlsTouched(tripRequestFg);
                return false;
            }
        }
        return true;
    }

    private markLocationControlsTouched(tripRequestFg: FormGroup): void {
        tripRequestFg.get('appointmentTypeId')?.markAsTouched();
        tripRequestFg.get('appointmentType')?.markAsTouched();
        const addressFa = tripRequestFg.get('tripAddresses') as FormArray;
        addressFa.markAllAsTouched();
    }

    saveTripLocation(tripRequestFg: FormGroup, tripType: TripRequestType): Observable<TripRequestDto> {
        const tripRqModel: TripRequestDto = {
            tripRequestId: tripRequestFg.get('tripRequestId')?.value,
            recipientId: this.selectedProfileService.selectedRecipientId()!,
            tripType: tripType,
            tripRequestStatusId: tripRequestFg.get('tripRequestStatus')?.value,
            appointmentTypeId: tripRequestFg.get('appointmentTypeId')?.value,
            appointmentType: tripRequestFg.get('appointmentType')?.value,
            isRoundTrip: tripRequestFg.get('isRoundTrip')?.value,
            stepIndex: tripType === TripRequestType.Pre ? 2 : 3
        };

        const tripAddresses = tripRequestFg.get('tripAddresses') as FormArray;
        const addressDto = tripAddresses?.value as TripAddressDto[];
        tripRqModel.tripAddresses = addressDto;

        return this.tripRequestService.saveTripLocation(tripRqModel);
    }

    validatePassenger(tripRequestFg: FormGroup): boolean {
        if (tripRequestFg.get('anotherMedicaidConsumer')?.value) {

            const tripAppointments = tripRequestFg.get('tripAppointments') as FormArray;
            const tripPassengers = tripRequestFg.get('tripPassengers') as FormArray;

            if (tripAppointments.length == 1 || tripRequestFg.get('samePassenger')?.value) {
                for (let i = 0; i <= tripPassengers.length - 1; i++) {
                    if (tripPassengers.at(i).get('firstName')?.invalid || tripPassengers.at(i).get('lastName')?.invalid) {
                        tripPassengers.markAllAsTouched();
                        return false;
                    }
                }
            }
            else {
                
                for (let i = 0; i <= tripAppointments.length - 1; i++) {
                    const triAppPassengers = tripAppointments.at(i).get('appointmentPassengers') as FormArray;
                    for (let j = 0; j <= triAppPassengers.length - 1; j++) {
                        if (triAppPassengers.at(j).get('firstName')?.invalid || triAppPassengers.at(j).get('lastName')?.invalid) {
                            triAppPassengers.markAllAsTouched();
                            return false;
                        }
                    }
                }
            }
        }
        return true;
    }

    savePassengers(tripRequestFg: FormGroup): Observable<TripRequestDto> {
        const tripRqModel: TripRequestDto = {
            tripRequestId: tripRequestFg.get('tripRequestId')?.value,
            recipientId: this.selectedProfileService.selectedRecipientId()!,
            tripType: TripRequestType.Post,
            tripRequestStatusId: tripRequestFg.get('tripRequestStatus')?.value,
            anotherMedicaidConsumer: tripRequestFg.get('anotherMedicaidConsumer')?.value,
            samePassenger: tripRequestFg.get('samePassenger')?.value,
            stepIndex: 4
        };

        if (tripRequestFg.get('anotherMedicaidConsumer')?.value) {

            if (tripRequestFg.get('samePassenger')?.value) {
                const tripPassengersFr = tripRequestFg.get('tripPassengers') as FormArray;
                const passengers = tripPassengersFr?.value as TripPassengerDto[];
                tripRqModel.tripPassengers = passengers;
            }
            else {
                const appointmentRqModel: TripAppointmentDto[] = [];
                const tripAppointments = tripRequestFg.get('tripAppointments') as FormArray;

                tripAppointments.controls.forEach((control: AbstractControl, i) => {
                    const appointment: TripAppointmentDto = {
                    appointmentId: control.get('appointmentId')?.value,
                    appointmentDate: control.get('appointmentDate')?.value,
                    appointmentTime: control.get('appointmentTime')?.value,
                    };
                    appointmentRqModel.push(appointment);

                    const appPassengersFr = control.get('appointmentPassengers') as FormArray;
                    const appPassengers = appPassengersFr?.value as TripPassengerDto[];
                    appointmentRqModel[i].appointmentPassengers = appPassengers;
                });

                tripRqModel.tripAppointments = appointmentRqModel;
            }
        }
        return this.tripRequestService.saveTripPassenger(tripRqModel);
    }

    loadAgentTripLegs(appointmentFr: FormArray, legs: TripLegsDto[], tripType: TripRequestType ) {
        appointmentFr.controls.forEach((control: AbstractControl, i) => {
            const legFr = appointmentFr.at(i).get('tripLegs') as FormArray;
            let toltalMiles = 0;
            appointmentFr.at(i).get('totalMiles')?.setValue(0);
            appointmentFr.at(i).get('totalMileageAmount')?.setValue(0);
            appointmentFr.at(i).get('showExplanationText')?.setValue(false);
            appointmentFr.at(i).get('explanationText')?.setValue('');
            legs?.forEach((leg, j) => {
                this.loadTripLegs(appointmentFr, legFr, leg, j, i, tripType);
                if (tripType === TripRequestType.Pre) {
                    toltalMiles = toltalMiles + leg.shortestMiles;                 
                }
            });
            if (tripType === TripRequestType.Pre) {
                appointmentFr.at(i).get('totalMiles')?.setValue(toltalMiles);                
            }
        });
    }

    loadTripLegs(appointmentFr: FormArray, legFr: FormArray, leg: TripLegsDto, j: number, i: number, tripType: TripRequestType) {
        if (!!legFr.at(j)) {
            legFr.at(j).patchValue( {
                legId: leg.legId,
                seqNo: leg.seqNo,
                fromAddressId: leg.fromAddressId,
                toAddressId: leg.toAddressId,
                shortestMiles: leg.shortestMiles,
                enteredMiles:leg.shortestMiles,
                approvedMiles:''
            })
        }
        else {
            const legFb = this.fb.group({
                legId: [leg.legId],
                seqNo: [leg.seqNo],
                fromAddressId: [leg.fromAddressId],
                toAddressId: [leg.toAddressId],
                shortestMiles: [leg.shortestMiles],
                enteredMiles: ['', [Validators.required]],
                approvedMiles: [''],
            });
            legFr.push(legFb);
            if (tripType === TripRequestType.Post) {
                const milesCtl = legFb.get('enteredMiles') as FormControl;
                const sub = milesCtl.valueChanges.subscribe((value) => {
                    const legs = appointmentFr.at(i).get('tripLegs') as FormArray;
                    let total = 0;
                    legs.controls.forEach((control) => {
                        total = total + Number(control.get('enteredMiles')?.value);
                    });
                    appointmentFr.at(i).get('totalMiles')?.setValue(total);
                    appointmentFr.at(i).get('totalMileageAmount')?.setValue(total * this.tripRate);
                });
                this.consumerEnteredMilesSub.push(sub);
                milesCtl.setValue(leg.shortestMiles);
            }
        }
    }

    loadPostTripLegs(appointmentFr: FormArray, data: TripRequestDto) {
        data.tripAppointments?.forEach((appointment, i) => {
            const legFr = appointmentFr.at(i).get('tripLegs') as FormArray;
            appointmentFr.at(i).get('totalMiles')?.setValue(0);
            appointmentFr.at(i).get('totalMileageAmount')?.setValue(0);
            appointmentFr.at(i).get('showExplanationText')?.setValue(false);
            appointmentFr.at(i).get('explanationText')?.setValue('');
            appointment.tripLegs?.forEach((leg, j) => {
                this.loadTripLegs(appointmentFr, legFr, leg, j, i, TripRequestType.Post);
            });
        });
    }

    loadPreTripLegs(appointmentFr: FormArray, data: TripRequestDto) {
        data.tripAppointments?.forEach((appointment, i) => {
            const legFr = appointmentFr.at(i).get('tripLegs') as FormArray;
            appointmentFr.at(i).get('totalMiles')?.setValue(0);
            let toltalMiles = 0;
            appointment.tripLegs?.forEach((leg, j) => {
                this.loadTripLegs(appointmentFr, legFr, leg, j, i, TripRequestType.Pre);
                toltalMiles = toltalMiles + leg.shortestMiles;
            });
            appointmentFr.at(i).get('totalMiles')?.setValue(toltalMiles);
        });
    }

    validateTripSummary(tripRequestFg: FormGroup, tripType: TripRequestType): boolean {
        
        let isSummaryValid = true;

        if (tripRequestFg.get('tripAuthorize')?.invalid || !tripRequestFg.get('tripAuthorize')?.value) {
            tripRequestFg.get('tripAuthorize')?.markAsTouched();
            isSummaryValid = false;
        }

        if (tripType === TripRequestType.Post) {
            const tripAppointments = tripRequestFg.get('tripAppointments') as FormArray;

            for (let i = 0; i <= tripAppointments.length - 1; i++) {
                const tripLegs = tripAppointments.at(i).get('tripLegs') as FormArray;
                const hasInvalidLeg = tripLegs.controls.some((leg) => leg.get('enteredMiles')?.invalid);
                if (hasInvalidLeg) {
                    tripLegs.markAllAsTouched();
                    isSummaryValid = false;
                }

                const hasDifferentMiles = tripLegs.controls.some((leg) => leg.get('enteredMiles')?.value !== leg.get('shortestMiles')?.value);
                if (hasDifferentMiles) {
                    tripAppointments.at(i).get('explanationText')?.markAllAsTouched();
                    if (tripAppointments.at(i).get('explanationText')?.invalid) {
                        isSummaryValid = false;
                    }
                }
            }
        }
        return isSummaryValid;
    }

    saveTripSummary(tripRequestFg: FormGroup, tripType: TripRequestType): Observable<string> {
        const tripRqModel: TripRequestDto = {
            tripRequestId: tripRequestFg.get('tripRequestId')?.value,
            recipientId: this.selectedProfileService.selectedRecipientId()!,
            tripType: tripType,
            tripRequestStatusId: TripRequestStatus.SubmittedByConsumer,
            queueStatusMappingId: this.authService.isCitizen() ? TripQueueMapping.SubmittedByConsumer : this.authService.isBusinessPartner() ? TripQueueMapping.SubmittedByAgency : null,
            stepIndex: tripType === TripRequestType.Pre ? 2 : 5
        };

        const appointmentRqModel: TripAppointmentDto[] = [];
        const tripAppointments = tripRequestFg.get('tripAppointments') as FormArray;
        let expenseAmount = 0;
        tripAppointments.controls.forEach((control, i) => {
            expenseAmount = expenseAmount + Number(control.get('expenseAmount')?.value);
            const TripLegsDto: TripLegsDto[] = [];
            const appointment: TripAppointmentDto = {
                appointmentId: control.get('appointmentId')?.value,
                appointmentDate: control.get('appointmentDate')?.value,
                appointmentTime: control.get('appointmentTime')?.value,
                enteredTotalMiles: control.get('totalMiles')?.value,
                enteredReimbursementAmount: control.get('totalMileageAmount')?.value,
                enteredTotalReimbursementAmount: control.get('expenseAmount')?.value,
                explanationText: control.get('explanationText')?.value
            };
            appointmentRqModel.push(appointment);
            const tripLegsFr = control.get('tripLegs') as FormArray;

            tripLegsFr.controls.forEach((control) => {
                const tripLeg: TripLegsDto = {
                    legId: control.get('legId')?.value,
                    seqNo: control.get('seqNo')?.value,
                    fromAddressId: control.get('fromAddressId')?.value,
                    toAddressId: control.get('toAddressId')?.value,
                    shortestMiles: control.get('shortestMiles')?.value,
                };

                if (tripType === TripRequestType.Post) {
                    tripLeg.enteredMiles = control.get('enteredMiles')?.value;
                }
                TripLegsDto.push(tripLeg);
            });
            appointmentRqModel[i].tripLegs = TripLegsDto;
        });
        tripRqModel.expenseAmount = expenseAmount;
        tripRqModel.tripAppointments = appointmentRqModel;
        return this.tripRequestService.saveTripSummary(tripRqModel);
    }



    validateSelectedAddress(fg: FormArray, value: string, index: number): void {
    
        let addresses = value.split(',') as string[];
        addresses = addresses.map((element) => element.trim());
    
        const addessFg = fg.at(index) as FormGroup;
        const addressControl = addessFg.get('address') as FormControl;

        if (this.lookupService.getStateCodes().some(sateCode => addresses.includes(sateCode))) {
            addressControl.setValidators([Validators.required]);
        }
        else {
            addressControl.addValidators(multyStateCodesValidator(this.lookupService.getStateCodes()));
            addressControl.setValue(value);
            addressControl.setValidators([Validators.required]);
        }
    }

    getCountyInfo() {
        const countyCode = this.selectedProfileService.selectedCountyCode()!;
        this.lookupService.getCountyByCode(countyCode).subscribe(data => {
          this.consumerCounty = data;
        })
    }


}